import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { loadDigitalCampaignLanding } from "../services/digitalCampaignLandings";
import "./DigitalCampaignLanding.css";

const INTENT_KEY = "romi:public-digital-checkout-intent:v1";
const CTA_KEYS = new Set(["HERO", "OFERTA_CONFIANZA", "CTA_FINAL"]);

function readIntent() {
  try {
    const value = JSON.parse(localStorage.getItem(INTENT_KEY) || "null");
    return value && value.checkoutIntent === true && typeof value.slug === "string" && Number.isFinite(value.timestamp) ? value : null;
  } catch { return null; }
}

function formatPrice(offer) {
  if (!offer || offer.price === null) return "";
  const amount = new Intl.NumberFormat("es-EC", { minimumFractionDigits: Number.isInteger(offer.price) ? 0 : 2, maximumFractionDigits: 2 }).format(offer.price);
  return `${offer.currency === "USD" ? "$" : ""}${amount} ${offer.currency}`;
}

function DigitalImage({ section, product }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className="digital-public-image-fallback" role="img" aria-label={`Visual no disponible para ${section.headline || product}`}><span>Visual no disponible</span></div>;
  const hero = section.key === "HERO";
  return <img src={section.image} alt={`${section.headline || section.key} — ${product}`} loading={hero ? "eager" : "lazy"} fetchPriority={hero ? "high" : "auto"} decoding="async" onError={() => setFailed(true)} />;
}

function usePublicMeta(landing) {
  useEffect(() => {
    if (!landing) return;
    document.title = landing.metadata.title;
    const values = { description: landing.metadata.description, "og:title": landing.metadata.title, "og:description": landing.metadata.description, "og:image": new URL(landing.sections[0].image, window.location.origin).href, "og:url": `${window.location.origin}/d/${landing.slug}`, "og:type": "product" };
    Object.entries(values).forEach(([name, content]) => {
      if (!content) return;
      const attr = name.startsWith("og:") ? "property" : "name";
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) { element = document.createElement("meta"); element.setAttribute(attr, name); document.head.appendChild(element); }
      element.setAttribute("content", content);
    });
  }, [landing]);
}

function Recovery({ landing, intent, onDismiss }) {
  const offer = landing.offers.find((item) => item.ref === intent.offerRef);
  if (!offer) return null;
  const continuePurchase = () => window.location.assign(offer.hotlink);
  return <div className="digital-public-recovery-host" data-testid="digital-recovery">
    <aside className="digital-public-recovery" role="dialog" aria-modal="false" aria-labelledby="digital-recovery-title">
      <button type="button" className="digital-public-recovery-close" onClick={onDismiss} aria-label="Cerrar ayuda de compra">×</button>
      <span>RETOMA CUANDO QUIERAS</span><h2 id="digital-recovery-title">¿Quieres continuar?</h2>
      {formatPrice(offer) && <strong>{formatPrice(offer)}</strong>}
      {landing.recovery.facts.length > 0 && <ul>{landing.recovery.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul>}
      <button type="button" className="digital-public-cta" onClick={continuePurchase}>Continuar con mi compra</button>
      <button type="button" className="digital-public-recovery-later" onClick={onDismiss}>Ahora no</button>
    </aside>
    <button type="button" className="digital-public-recovery-sticky" onClick={continuePurchase}>Continuar con mi compra</button>
  </div>;
}

export default function DigitalCampaignLanding() {
  const { slug = "" } = useParams();
  const [state, setState] = useState({ slug, landing: null, loading: true });
  const [activeIntent, setActiveIntent] = useState(null);
  const landing = state.slug === slug ? state.landing : null;
  const loading = state.slug === slug ? state.loading : true;
  usePublicMeta(landing);

  useEffect(() => {
    const controller = new AbortController();
    setState({ slug, landing: null, loading: true });
    loadDigitalCampaignLanding(slug, { signal: controller.signal }).then((loaded) => {
      if (!controller.signal.aborted) setState({ slug, landing: loaded, loading: false });
    });
    return () => controller.abort();
  }, [slug]);

  const detectRecovery = useCallback(() => {
    if (!landing) return;
    const intent = readIntent();
    if (!intent || intent.slug !== landing.slug || Date.now() - intent.timestamp > landing.recovery.expiresAfterMs) {
      if (intent && Date.now() - intent.timestamp > landing.recovery.expiresAfterMs) {
        try { localStorage.removeItem(INTENT_KEY); sessionStorage.removeItem(`${INTENT_KEY}:dismissed:${intent.timestamp}`); } catch { /* storage opcional */ }
      }
      setActiveIntent(null); return;
    }
    try { if (sessionStorage.getItem(`${INTENT_KEY}:dismissed:${intent.timestamp}`) === "1") { setActiveIntent(null); return; } } catch { /* storage opcional */ }
    setActiveIntent(intent);
  }, [landing]);

  useEffect(() => {
    detectRecovery();
    const onPageShow = () => detectRecovery();
    const onVisibility = () => { if (document.visibilityState === "visible") detectRecovery(); };
    window.addEventListener("pageshow", onPageShow); document.addEventListener("visibilitychange", onVisibility);
    return () => { window.removeEventListener("pageshow", onPageShow); document.removeEventListener("visibilitychange", onVisibility); };
  }, [detectRecovery]);

  const [stickyVisible, setStickyVisible] = useState(false);
  const offers = useMemo(() => new Map((landing?.offers || []).map((offer) => [offer.ref, offer])), [landing]);
  const primarySection = landing?.sections.find((section) => section.key === "HERO" && section.cta) || null;
  const primaryOffer = primarySection?.cta ? offers.get(primarySection.cta.offerRef) : null;

  useEffect(() => {
    if (!primarySection || !primaryOffer) { setStickyVisible(false); return; }
    const heroCta = document.querySelector(".digital-public-hero .digital-public-cta");
    if (!heroCta || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setStickyVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" }
    );
    observer.observe(heroCta);
    return () => observer.disconnect();
  }, [primarySection, primaryOffer, landing]);

  const openOffer = (section) => {
    const offer = section.cta ? offers.get(section.cta.offerRef) : null;
    if (!offer) return;
    const intent = { slug: landing.slug, offerRef: offer.ref, sectionKey: section.key, timestamp: Date.now(), checkoutIntent: true };
    try { localStorage.setItem(INTENT_KEY, JSON.stringify(intent)); } catch { /* Nunca bloquea el CTA. */ }
    window.location.assign(offer.hotlink);
  };
  const dismissRecovery = () => {
    const intent = readIntent();
    try { if (intent) sessionStorage.setItem(`${INTENT_KEY}:dismissed:${intent.timestamp}`, "1"); } catch { /* storage opcional */ }
    setActiveIntent(null);
  };

  if (loading) return <main className="digital-public-status"><span>Cargando landing…</span></main>;
  if (!landing) return <main className="digital-public-status"><span id="snap-ready" hidden /><h1>Esta landing ya no está disponible</h1><p>El contenido público fue retirado o no existe.</p><a href="/">Volver a ROMI STORE</a></main>;
  return <main className={`digital-public-page digital-theme-${landing.visualSystem}`} data-visual-system={landing.visualSystem.toUpperCase()}>
    <span id="snap-ready" hidden />
    <div className="digital-public-shell">
      {landing.sections.map((section) => {
        const offer = section.cta ? offers.get(section.cta.offerRef) : null;
        const headingId = `digital-public-heading-${section.key.toLowerCase()}`;
        return <section aria-labelledby={headingId} className={`digital-public-section digital-public-${section.key.toLowerCase()} ${landing.imageContainsCopy ? "image-has-copy" : ""}`} key={section.key}>
          <div className="digital-public-section-inner">
            <figure className="digital-public-image"><DigitalImage section={section} product={landing.productoNombre} /></figure>
            <div className="digital-public-content">
              <div className={landing.imageContainsCopy ? "digital-public-copy-in-image" : ""}>
                <h1 id={headingId}>{section.headline}</h1>{section.subtitle && <p className="digital-public-subtitle">{section.subtitle}</p>}
                {section.chips.length > 0 && <div className="digital-public-chips">{section.chips.map((chip) => <span key={chip}>{chip}</span>)}</div>}
              </div>
              {section.key === "OFERTA_CONFIANZA" && formatPrice(offer) && <strong className="digital-public-price">{formatPrice(offer)}</strong>}
              {section.body.map((paragraph) => <p className="digital-public-body" key={paragraph}>{paragraph}</p>)}
              {section.key === "OFERTA_CONFIANZA" && landing.recovery.facts.length > 0 && <ul className="digital-public-benefits">{landing.recovery.facts.slice(0, 4).map((fact) => <li key={fact}>{fact}</li>)}</ul>}
              {CTA_KEYS.has(section.key) && section.cta && offer && <button type="button" className="digital-public-cta" onClick={() => openOffer(section)}>{section.cta.label}</button>}
            </div>
          </div>
        </section>;
      })}
    </div>
    {primarySection && primaryOffer && !activeIntent && <button type="button" className={`digital-public-mobile-sticky${stickyVisible ? " is-visible" : ""}`} onClick={() => openOffer(primarySection)}>{primarySection.cta.label}</button>}
    {activeIntent && <Recovery landing={landing} intent={activeIntent} onDismiss={dismissRecovery} />}
  </main>;
}
